import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  DotsThreeVertical,
  Trash,
  ArrowRight,
  ArrowLeft,
} from "@phosphor-icons/react";
import { type ActionResult, type ContextualAction } from "scantling-common";
import { ActionMenuItem } from "./action_menu.js";
import { ActionButton } from "./action_button.js";

function getActionIcon(action: ContextualAction) {
  switch (action.icon) {
    case "arrowLeft":
      return <ArrowLeft size={14} className="mr-2" />;
    case "arrowRight":
      return <ArrowRight size={14} className="mr-2" />;
    case "trash":
      return <Trash size={14} className="mr-2" />;
    case undefined:
      return null;
    default:
      throw new Error(`Unknown icon ${action.icon}`);
  }
}

export interface ContextualActionsWidgetProps {
  actions: ContextualAction[];
  onActionSuccess?: (action: ContextualAction, result: ActionResult) => void;
  onActionError?: (action: ContextualAction, error: Error) => void;
}

export function ContextualActionsWidget({
  actions,
  onActionSuccess,
  onActionError,
}: ContextualActionsWidgetProps) {
  const topAction = actions.find(
    a => a.variant !== "destructive" && !a.argument,
  );
  const remainingActions = topAction
    ? actions.filter(a => a !== topAction)
    : actions;
  return (
    <div className="flex items-center gap-1">
      {topAction && (
        <ActionButton
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs"
          verb={topAction.verb}
          params={topAction.params}
          onActionSuccess={result => onActionSuccess?.(topAction, result)}
          onActionError={error => onActionError?.(topAction, error)}
        >
          {getActionIcon(topAction)}
          {topAction.label}
        </ActionButton>
      )}
      {remainingActions.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-6 w-6">
              <DotsThreeVertical size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {remainingActions.map(action => (
              <ActionMenuItem
                key={`${action.verb}-${JSON.stringify(action.params)}`}
                action={action}
                onActionSuccess={onActionSuccess}
                onActionError={onActionError}
                getIcon={getActionIcon}
              />
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
